import { DocumentCheckIcon, DocumentMinusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Badge, Button, Column, Paragraph, Small, Table, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  useLearnerByIdQuery,
  useUnassignCaregiverFromLearnerMutation,
  useUpdateCaregiverConsentsMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import {
  AddUpdateCaregiverDialogue,
  caregiverListType,
} from "@pages/ClientDetailsSubPages/Caregivers/AddUpdateCaregiverDialogue";
import { useAdminData } from "@providers/AdminDataProvider";

export const ClientDetailsCaregivers: React.FC = () => {
  const { clientId } = useParams();
  const flags = useFlags();
  const [isNewCaregiverDialogueOpen, setIsNewCaregiverDialogueOpen] = useState<boolean>(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState<caregiverListType | null>(null);
  const [caregiverList, setCaregiverList] = useState<caregiverListType[]>([]);

  const queryClient = useQueryClient();
  const { isConsentRequired } = useAdminData();
  const learnerDetailsQuery = useLearnerByIdQuery(
    { learnerId: clientId ? clientId : "" },
    {
      queryKey: ["learner-by-id", clientId],
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const { data, refetch, isFetching } = learnerDetailsQuery;

  const { mutateAsync: updateCaregiverConsents } = useUpdateCaregiverConsentsMutation();

  const { mutate: unassignCaregiver } = useUnassignCaregiverFromLearnerMutation({
    onSuccess: (data) => {
      if (data.unassignCaregiverFromLearner?.status) {
        notifySuccess(data?.unassignCaregiverFromLearner?.message || "Caregiver unassigned successfully");
        refetch();
      } else {
        notifyError("Failed to remove caregiver!");
      }
    },
  });

  useEffect(() => {
    if (data && data.learnerById) {
      const caregivers = data.learnerById?.relationshipSet.edges.map((edge) => {
        const relationship = edge?.node;
        const caregiver = relationship?.caregiver;
        const caregiverMetadata = caregiver?.caregivermetadata;
        const inviteStatus = caregiver?.caregivermetadata?.invitationStatus;

        return {
          caregiverId: caregiver?.id || "",
          firstName: caregiverMetadata?.firstName || "",
          lastName: caregiverMetadata?.lastName || "",
          email: caregiverMetadata?.email || "",
          phoneNumber1: caregiverMetadata?.phoneNumber1 || "",
          nearestLocation: caregiverMetadata?.nearestLocation || "",
          referralSource: caregiverMetadata?.referralSource || "",
          smsAgreement: caregiverMetadata?.smsAgreement || false,
          intakeInvite: inviteStatus || null,
          invitationUrl: caregiver?.caregivermetadata?.invitationUrl || null,
          auth0Id: caregiver?.auth0User?.auth0id || null,
          isPrimary: relationship?.isPrimary || false,
          consentGiven: relationship?.hasCaregiverConsent,
          isAllowedAppAccess: caregiver?.isAllowedAppAccess || false,
          hasEnteredVideoCaptureApp: caregiver?.hasEnteredVideoCaptureApp || false,
        };
      });

      setCaregiverList(caregivers || []);
    }
  }, [data]);

  const columns: Column<caregiverListType>[] = [
    {
      title: "Caregiver Name",
      key: "caregiver_name",
      dataIndex: "firstName",
      render: (_, record) => {
        return (
          <div className="flex flex-row gap-2">
            <Small displayType="loud" className="ph-no-capture">
              {record.firstName} {record.lastName}
            </Small>
            {record.isPrimary ? <Badge appearance="success" text="Primary" /> : null}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber1",
      key: "phone",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Has App Access",
      dataIndex: "isAllowedAppAccess",
      key: "isAllowedAppAccess",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Started Using App",
      dataIndex: "hasEnteredVideoCaptureApp",
      key: "hasEnteredVideoCaptureApp",
      render: (value) => (value ? "Yes" : "No"),
    },
  ];

  if (isConsentRequired) {
    columns.push({
      dataIndex: "consentGiven",
      key: "consentGiven",
      title: (
        <div className="flex flex-row gap-2 items-center">
          Video Consent
          <Tooltip
            text="Video recording is enabled if at least one caregiver has signed a video consent. Consent distribution and tracking is managed by your clinic."
            placement="top"
          />
        </div>
      ),
      render: (value) => (value ? "Signed" : "Not Signed"),
    });
  }

  const filteredColumn = flags["parent-app"]
    ? columns
    : columns.filter((item) => item.dataIndex !== "invitationUrl" && item.dataIndex !== "intakeInvite");

  return (
    <div className="flex w-full overflow-x-hidden flex-col items-start gap-4 py-4">
      <div className="flex w-full justify-end">
        <Button
          text="Add Caregiver"
          variant="primary"
          size="md"
          buttonStyle="border"
          onClick={() => {
            setSelectedCaregiver(null);
            setIsNewCaregiverDialogueOpen(true);
          }}
          disabled={isFetching}
        />
      </div>
      <div className="flow-root w-full">
        <Table
          data={caregiverList}
          columns={filteredColumn}
          loading={isFetching}
          onRowClick={(rowData) => {
            setSelectedCaregiver(rowData);
          }}
          actions={(caregiver) => [
            isConsentRequired
              ? {
                  label: caregiver.consentGiven ? "Unmark as signed" : "Mark consent as signed",
                  Icon: caregiver.consentGiven ? DocumentMinusIcon : DocumentCheckIcon,
                  callback: async (caregiver) => {
                    await updateCaregiverConsents({
                      consentUpdates: [
                        {
                          learnerId: clientId!,
                          caregiverId: caregiver.caregiverId!,
                          consentGiven: !caregiver.consentGiven,
                        },
                      ],
                    });
                    await queryClient.invalidateQueries({ queryKey: ["learner-by-id", clientId] });
                    await queryClient.invalidateQueries({ queryKey: ["learners"] });
                  },
                }
              : false,
            {
              label: "View / Edit",
              Icon: PencilIcon,
              callback: (caregiver) => {
                setSelectedCaregiver(caregiver);
                setIsNewCaregiverDialogueOpen(true);
              },
            },
            {
              label: "Unassign Caregiver",
              Icon: TrashIcon,
              callback: (caregiver) => {
                unassignCaregiver({
                  learnerId: clientId!,
                  caregiverId: caregiver.caregiverId!,
                });
              },
            },
          ]}
          emptyMessage={
            <div className="flex flex-col items-center">
              <Paragraph displayType="loud">No Caregivers Added Yet</Paragraph>
              <Small>Caregivers added to this client will show here</Small>
            </div>
          }
          showPagination
          showSearch
        />
      </div>

      <AddUpdateCaregiverDialogue
        isDialogOpen={isNewCaregiverDialogueOpen}
        setDialogOpen={setIsNewCaregiverDialogueOpen}
        onAddCaregiver={() => {
          setSelectedCaregiver(null);
          refetch();
        }}
        key={Date.now()}
        selectedCaregiver={selectedCaregiver}
        clientId={clientId}
        isPrimaryCaregiver={caregiverList.length === 0 || (selectedCaregiver && caregiverList.length === 1)}
        assignedCaregivers={caregiverList}
      />
    </div>
  );
};
